import { ResellerStatusFilter } from "@kilat/contracts/reseller";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { ListPagination, paginate } from "@/components/list-pagination";
import { Item, ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";
import { resellersQuery } from "@/features/staff/queries";
import {
  arrangeResellers,
  DEFAULT_RESELLER_SEARCH,
  RESELLER_SORTS,
  ResellerTable,
  SORT_ORDERS,
  type ResellerSearch,
} from "@/features/staff/reseller-table";
import { m } from "@/lib/paraglide/messages.js";

const PAGE_SIZE = 10;

// The search, the status filter, the sort and the page are route state (guide, rule 16): a link
// to "rejected, by company" opens just that. Anything the list does not know reads as its default.
const ResellersSearch = z.object({
  q: z.string().max(100).optional().catch(undefined),
  status: ResellerStatusFilter.optional().catch(undefined),
  sort: z.enum(RESELLER_SORTS).optional().catch(undefined),
  order: z.enum(SORT_ORDERS).optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});

// /staff/resellers: every reseller, whatever its status. The API narrows by status; it sends
// every reseller with it, so the search, the sort and the page are applied here.
export const Route = createFileRoute("/staff/_authenticated/resellers/")({
  validateSearch: ResellersSearch,
  loaderDeps: ({ search }) => ({ status: search.status ?? DEFAULT_RESELLER_SEARCH.status }),
  loader: ({ context, deps }) => context.queryClient.ensureQueryData(resellersQuery(deps.status)),
  component: ResellersPage,
});

function ResellersPage() {
  const { page: asked = 1, q, status, sort, order } = Route.useSearch();
  const navigate = Route.useNavigate();
  // Field by field: a value the schema caught is there as undefined, and must not hide the default.
  const search: ResellerSearch = {
    q: q ?? DEFAULT_RESELLER_SEARCH.q,
    status: status ?? DEFAULT_RESELLER_SEARCH.status,
    sort: sort ?? DEFAULT_RESELLER_SEARCH.sort,
    order: order ?? DEFAULT_RESELLER_SEARCH.order,
  };
  const { data: resellers } = useSuspenseQuery(resellersQuery(search.status));
  const { page, pages, items, range } = paginate(arrangeResellers(resellers, search), asked, PAGE_SIZE);
  // A changed search starts again from the first page; an emptied search box leaves the URL.
  // Replaced, not pushed: Back leaves the list rather than undoing each keystroke.
  const onSearchChange = (next: Partial<ResellerSearch>) =>
    navigate({
      search: (prev) => ({ ...prev, ...next, q: (next.q ?? prev.q) || undefined, page: undefined }),
      replace: true,
    });
  return (
    <>
      <Item>
        <ItemContent>
          <ItemTitle>{m.staff_nav_resellers()}</ItemTitle>
          <ItemDescription>{m.staff_resellers_page_description()}</ItemDescription>
        </ItemContent>
      </Item>
      <ResellerTable
        resellers={items}
        search={search}
        onSearchChange={onSearchChange}
        caption={range}
        pagination={<ListPagination page={page} pages={pages} />}
      />
    </>
  );
}
